<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <div class="pt-4 pb-15">
    <!-- 日付範囲フォーム -->
    <div class="d-flex align-center flex-wrap mb-4 px-4" style="gap: 8px;">
      <v-menu
        v-model="startDateMenu"
        transition="scale-transition"
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="startDate"
            label="開始日"
            :prepend-icon="icons.mdiCalendar"
            readonly
            hide-details
            style="max-width: 200px;"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="startDate"
          locale="ja"
          :min="periodMinDate"
          :day-format="d => new Date(d).getDate()"
          color="primary"
          no-title
          @input="startDateMenu = false"
        />
      </v-menu>

      <span class="mx-2">〜</span>

      <v-menu
        v-model="endDateMenu"
        transition="scale-transition"
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="endDate"
            label="終了日"
            :prepend-icon="icons.mdiCalendar"
            readonly
            hide-details
            style="max-width: 200px;"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="endDate"
          locale="ja"
          :min="periodMinDate"
          :day-format="d => new Date(d).getDate()"
          color="primary"
          no-title
          @input="endDateMenu = false"
        />
      </v-menu>

      <v-btn
        color="primary"
        :ripple="false"
        @click="applyDateRange"
      >
        適用
      </v-btn>

      <v-spacer />

      <download-buttons
        :csv-loading="csvLoading"
        :xlsx-loading="xlsxLoading"
        @download-csv="handleDownloadCsv"
        @download-xlsx="handleDownloadXlsx"
      />
    </div>

    <!-- ローディング / エラー -->
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
      class="px-4"
    />

    <template v-else-if="fetchError">
      <v-alert
        type="error"
        class="mx-4"
      >
        {{ fetchError }}
      </v-alert>
    </template>

    <template v-else>
      <!-- 月次サマリ(会計連携用)。ポータル版と同一の5値 -->
      <v-card
        v-if="summary"
        class="mb-4"
        outlined
      >
        <v-card-title class="text-subtitle-1">
          月次サマリ（会計連携用）
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-wrap" style="gap: 24px;">
            <div>売上計: {{ formatSummaryMoney(summary.sales) }}</div>
            <div>経費計: {{ formatSummaryMoney(summary.expense) }}</div>
            <div>粗利: {{ formatSummaryMoney(summary.gross) }}</div>
            <div>雑損益（過不足）: {{ formatSummaryMoney(summary.misc) }}</div>
            <div class="font-weight-bold">
              差引利益（粗利＋雑損益）: {{ formatSummaryMoney(summary.net) }}
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- 発生/現金タブ -->
      <v-tabs
        v-model="activeTab"
        class="mb-4"
      >
        <v-tab>発生</v-tab>
        <v-tab>現金</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- 発生タブ -->
        <v-tab-item>
          <v-card class="mb-6">
            <v-card-title>収支明細（発生）</v-card-title>
            <v-card-text class="pa-0">
              <div class="overflow-x-auto">
                <accrual-table
                  :rows="accrualRows"
                  :totals="accrualTotals"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- 現金タブ -->
        <v-tab-item>
          <v-card class="mb-6">
            <v-card-title>収支明細（現金）</v-card-title>
            <v-card-text class="pa-0">
              <div class="overflow-x-auto">
                <cash-table
                  :rows="cashRows"
                  :totals="cashTotals"
                />
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

      <!-- 諸入費一覧 -->
      <v-card class="mx-0">
        <v-card-title>諸入費一覧</v-card-title>
        <v-card-text class="pa-0">
          <div class="overflow-x-auto">
            <benefits-expenses-table :items="benefitsAndExpenses" />
          </div>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import {
  ref,
  computed,
  inject,
  onMounted,
  watch,
} from '@vue/composition-api'
import { mdiCalendar } from '@mdi/js'
import { useRouter } from '@core/utils'
import useCompInit from '@/views/composable/useCompInit'
import { useAbilityEvaluate } from '@/views/v2-temp/common/composables/useAbilityEvaluate'
import { useDateRange } from '@/views/v2-temp/common/composables/useDateRange'
import MonthlyPlApi from '@/api/admin/MonthlyPl'
import AccrualTable from './components/AccrualTable.vue'
import CashTable from './components/CashTable.vue'
import BenefitsExpensesTable from './components/BenefitsExpensesTable.vue'
import DownloadButtons from './components/DownloadButtons.vue'

function getMonthStart(dateStr) {
  const d = new Date(dateStr)

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function getMonthEnd(dateStr) {
  const d = new Date(dateStr)
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0)

  return `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
}

export default {
  components: {
    AccrualTable,
    CashTable,
    BenefitsExpensesTable,
    DownloadButtons,
  },
  setup() {
    const { isLoading, initWith } = useCompInit()
    const { route, router } = useRouter()
    const currentClubMeta = inject('currentClubMeta', { today: null })
    const today = currentClubMeta.today || new Date().toISOString().slice(0, 10)

    const startDate = ref(route.value.query.start_date || getMonthStart(today))
    const endDate = ref(route.value.query.end_date || getMonthEnd(today))
    const startDateMenu = ref(false)
    const endDateMenu = ref(false)
    const activeTab = ref(0)
    const fetchError = ref(null)

    // 過去期間の参照制限: read_all_periods が無い場合はカレンダーを前月月初までに制限
    // (サーバ側の403ガードと対のフロント制御。eod-report等と同じパターン)
    const { canAccessPeriod } = useAbilityEvaluate()
    const { firstDayOfPreviousMonth } = useDateRange()
    const periodMinDate = computed(() => (canAccessPeriod() ? undefined : firstDayOfPreviousMonth()))

    const summary = ref(null)
    const accrualRows = ref([])
    const cashRows = ref([])
    const accrualTotals = ref({})
    const cashTotals = ref({})
    const benefitsAndExpenses = ref([])

    const csvLoading = ref(false)
    const xlsxLoading = ref(false)

    const fetchMonthlyPl = async () => {
      fetchError.value = null
      try {
        const res = await MonthlyPlApi.getMonthlyPl({
          startDate: startDate.value,
          endDate: endDate.value,
        })
        summary.value = res.data.summary || null
        accrualRows.value = res.data.accrualRows || []
        cashRows.value = res.data.cashRows || []
        accrualTotals.value = res.data.accrualTotals || {}
        cashTotals.value = res.data.cashTotals || {}
        benefitsAndExpenses.value = res.data.benefitsAndExpenses || []
      } catch (e) {
        fetchError.value = 'データの取得に失敗しました'
      }
    }

    const applyDateRange = () => {
      router.replace({
        query: {
          ...route.value.query,
          start_date: startDate.value,
          end_date: endDate.value,
        },
      })
    }

    watch(
      () => [route.value.query.start_date, route.value.query.end_date],
      ([newStart, newEnd]) => {
        if (newStart) startDate.value = newStart
        if (newEnd) endDate.value = newEnd
        initWith([fetchMonthlyPl()])
      },
    )

    const triggerDownload = (blob, filename) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    const handleDownloadCsv = async () => {
      csvLoading.value = true
      try {
        const res = await MonthlyPlApi.downloadCsv({
          startDate: startDate.value,
          endDate: endDate.value,
        })
        triggerDownload(res.data, `monthly_pl_${startDate.value}_${endDate.value}.csv`)
      } finally {
        csvLoading.value = false
      }
    }

    const handleDownloadXlsx = async () => {
      xlsxLoading.value = true
      try {
        const res = await MonthlyPlApi.downloadXlsx({
          startDate: startDate.value,
          endDate: endDate.value,
        })
        triggerDownload(res.data, `monthly_pl_${startDate.value}_${endDate.value}.xlsx`)
      } finally {
        xlsxLoading.value = false
      }
    }

    onMounted(() => {
      initWith([fetchMonthlyPl()])
    })

    const formatSummaryMoney = amount => {
      if (amount == null) return '-'

      return `¥${Number(amount).toLocaleString()}`
    }

    return {
      isLoading,
      periodMinDate,
      summary,
      formatSummaryMoney,
      fetchError,
      startDate,
      endDate,
      startDateMenu,
      endDateMenu,
      activeTab,
      accrualRows,
      cashRows,
      accrualTotals,
      cashTotals,
      benefitsAndExpenses,
      csvLoading,
      xlsxLoading,
      applyDateRange,
      handleDownloadCsv,
      handleDownloadXlsx,
      icons: {
        mdiCalendar,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  overflow-x: auto;

  // 列見出し・日付などが幅不足で折り返さないようにし、横スクロールで見せる
  ::v-deep table {
    th,
    td {
      white-space: nowrap;
    }
  }
}
</style>
